import { useMemo } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { useSearchParams } from 'react-router-dom';
import type { TopicSearchEntry } from '../search';
import { topicMatchesQuery } from '../search';
import type { TopicMetadata } from '../types';
import TopicMetadataCard from './TopicMetadataCard';

const NO_RESULTS_MAIN = 'No results found';
const NO_RESULTS_HINT = 'Try a different term or verse reference (e.g. 1.5.12).';

interface ThemeTopicListSearchProps {
    themeSlug: string;
    themeName: string;
    themeDescription: string;
    topics: TopicMetadata[];
    searchEntries: TopicSearchEntry[];
}

export default function ThemeTopicListSearch({
    themeSlug,
    themeName,
    themeDescription,
    topics,
    searchEntries,
}: ThemeTopicListSearchProps) {
    const shouldReduceMotion = useReducedMotion();
    const [searchParams] = useSearchParams();
    const query = searchParams.get('q') ?? '';
    const queryTrimmed = query.trim();

    const filteredTopics = useMemo(() => {
        if (!queryTrimmed) return topics;

        const entryBySlug = new Map(searchEntries.map((entry) => [entry.slug, entry]));
        return topics.filter((topic) => {
            const entry = entryBySlug.get(topic.slug);
            return entry ? topicMatchesQuery(entry, queryTrimmed) : false;
        });
    }, [topics, searchEntries, queryTrimmed]);

    const showNoResults = queryTrimmed.length > 0 && filteredTopics.length === 0;
    const searchActive = queryTrimmed.length > 0;

    return (
        <div className="m-8 min-h-screen pb-14 md:mx-28 md:my-10">
            {!searchActive && (
                <div className="mb-12">
                    <h1 className="mb-4 text-4xl font-bold text-darkBlue dark:text-lightBlue md:text-5xl">{themeName}</h1>
                    <p className="mb-6 text-xl leading-relaxed text-default-600 dark:text-default-400">{themeDescription}</p>
                    <p className="font-inter-numbers text-sm text-default-500">
                        {topics.length} {topics.length === 1 ? 'topic' : 'topics'} available
                    </p>
                </div>
            )}

            {showNoResults ? (
                <motion.div
                    className="flex flex-col items-center justify-center gap-2 py-12 text-center"
                    initial={shouldReduceMotion ? { opacity: 1 } : { opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: shouldReduceMotion ? 0 : 0.16, ease: 'easeOut' }}
                >
                    <p className="text-2xl font-semibold text-default-700 dark:text-default-300 md:text-3xl">
                        {NO_RESULTS_MAIN}
                    </p>
                    <p className="text-base text-default-500 dark:text-default-400">{NO_RESULTS_HINT}</p>
                </motion.div>
            ) : filteredTopics.length > 0 ? (
                <>
                    {queryTrimmed.length > 0 && (
                        <motion.div
                            className="mb-8 text-center"
                            initial={shouldReduceMotion ? { opacity: 1 } : { opacity: 0, y: 6 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: shouldReduceMotion ? 0 : 0.16, ease: 'easeOut' }}
                        >
                            <h2 className="mb-2 text-2xl font-bold text-darkBlue dark:text-lightBlue md:text-3xl">
                                Search Results for &quot;<span className="text-lightOrange">{queryTrimmed}</span>&quot;
                            </h2>
                            <p className="text-default-500 dark:text-default-400">
                                Found {filteredTopics.length} {filteredTopics.length === 1 ? 'topic' : 'topics'}
                            </p>
                        </motion.div>
                    )}
                    <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                        {filteredTopics.map((topic, index) => (
                            <motion.div
                                key={topic.slug}
                                initial={
                                    shouldReduceMotion || searchActive
                                        ? { opacity: 1 }
                                        : {
                                              opacity: 0,
                                              x: index % 2 === 0 ? -50 : 50,
                                              y: -50,
                                          }
                                }
                                animate={{ opacity: 1, x: 0, y: 0 }}
                                transition={{
                                    duration: shouldReduceMotion || searchActive ? 0 : 0.3,
                                    delay: shouldReduceMotion || searchActive ? 0 : index * 0.2,
                                }}
                            >
                                <TopicMetadataCard
                                    topic={topic}
                                    themeSlug={themeSlug}
                                    topicNumber={index + 1}
                                />
                            </motion.div>
                        ))}
                    </div>
                </>
            ) : (
                <p className="text-default-500 dark:text-default-400">No topics available yet.</p>
            )}
        </div>
    );
}
